import {getLocale} from '@epic-core/common';
import {generateRouteTo} from '../../utils/route';
import {AccountLinks, EpicGamesNav} from 'epic-games-nav/dist/client';
import {generateStateQueryString} from 'epic-games-nav-shared/dist';
import LoginActions from '../../actions/LoginActions';
import root from 'window-or-global';
import React, {useCallback} from 'react';
import styled from 'styled-components';
import queryString from 'query-string';

import {setCTA, setCtaDropdown} from './nav.links';
import {Account} from '../../stores';
import {FortniteLinkComponent} from './FortniteLinkComponent';
import {navTheme} from './nav.theme';
import {NavWrapperProps} from './nav.types';
import invariant from 'tiny-invariant';

const Styled = styled.div``;

const filterLinkedPlatforms = (
  dropdownLinks: AccountLinks['dropdownLinks'] = [],
  account?: Account
) => {
  return dropdownLinks.filter((link) => {
    switch (link.key) {
      case 'linkPsn': {
        return !account?.hasPsnLink;
      }
      case 'linkXbl': {
        return !account?.hasXblLink;
      }
      case 'linkNintendo': {
        return !account?.hasNintendoLink;
      }
      default: {
        return true;
      }
    }
  });
};

const addRedirectParam = (dropdownLinks: AccountLinks['dropdownLinks'], currentHref: string) => {
  return dropdownLinks?.map((link) => {
    switch (link.key) {
      case 'linkPsn':
      case 'linkXbl':
      case 'linkNintendo': {
        const href = link.hrefTemplate ?? '';
        const queryIndex = href.indexOf('?');
        const query = href.slice(queryIndex);
        const qs = queryString.parse(query);
        qs.redirectUrl = currentHref;
        const base = href.slice(0, queryIndex);
        invariant(!base.includes('?'), `URL base ${base} should not contain another query param`);
        link.hrefTemplate = `${base}?${queryString.stringify(qs)}`;
        return link;
      }
      default: {
        return link;
      }
    }
  });
};

export const NavWrapper = ({
  history,
  location,
  localizationStore,
  account,
  appConfigStore = {},
  mobile,
  ...config
}: NavWrapperProps): JSX.Element => {
  const locale = localizationStore?.locale || getLocale();
  const isLoggedIn = account?.isLoggedIn || false;
  const callToAction = setCTA(
    config.callToAction || {},
    history,
    location,
    mobile || {},
    isLoggedIn
  );

  const callToActionDropdown = setCtaDropdown(
    config.callToActionDropdown || {},
    history,
    location,
    mobile || {},
    isLoggedIn
  );

  const searchCallback = useCallback(
    (query) => {
      history.push(generateRouteTo(`/search?q=${query}`, location));
    },
    [history]
  );

  const transformedAccountLinks = {
    signInLinks: [
      {
        key: 'epic-sign-in',
        title: 'EPIC GAMES',
        href: `/login${generateStateQueryString(locale)}`,
        onClick(e) {
          e.preventDefault();
          LoginActions.requestLogin(location.pathname);
        },
      },
    ],
    signOutLink: {
      href: `/logout${generateStateQueryString(locale)}`,
      onClick: (e) => {
        e.preventDefault();
        LoginActions.requestLogout(location.pathname);
      },
    },
    dropdownLinks: addRedirectParam(
      filterLinkedPlatforms(config.accountLinks?.dropdownLinks),
      root.location.href
    ),
  };

  return (
    <Styled>
      <EpicGamesNav
        {...config}
        i18n={{
          locale,
          messages: localizationStore?.messages || {},
        }}
        theme={navTheme}
        links={config.links}
        isLoggedIn={account?.isLoggedIn}
        displayName={account?.displayName}
        hideCallToAction={config.hideCallToAction || !callToAction}
        callToAction={callToAction}
        callToActionDropdown={callToActionDropdown}
        accountLinks={transformedAccountLinks}
        logoLink={config.logoLink}
        localeOptions={localizationStore?.supportedLocales}
        locale={locale}
        onSearch={searchCallback}
        pcDownloadUrl={appConfigStore?.pcDownloadUrl}
        macDownloadUrl={appConfigStore?.macDownloadUrl}
        pathname={location.pathname.length > 0 ? location.pathname : '/'}
        LinkComponent={FortniteLinkComponent}
      />
    </Styled>
  );
};
